import React from 'react';
import { Image, Copy, Star, Hash, FileText, AlertCircle, Loader2 } from 'lucide-react';

const ImagePreviewPanel = ({ imagePreview, specialData, onCopy }) => {
  return (
    <div className="space-y-6">
      {/* Preview da Imagem */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="bg-gradient-to-r from-purple-500 to-indigo-500 h-1 -mx-6 -mt-6 mb-6 rounded-t-lg"></div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Image className="h-5 w-5 text-purple-600" />
          Preview da Imagem
        </h3>

        <div className="space-y-4">
          {imagePreview.isLoading ? (
            <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Carregando imagem...</p>
              </div>
            </div>
          ) : imagePreview.url && imagePreview.isValid ? (
            <div className="relative group">
              <img
                src={imagePreview.url}
                alt="Preview do especial"
                className="w-full h-48 object-cover rounded-lg border-2 border-purple-200 shadow-sm"
                onError={() => console.log('Erro ao carregar imagem')}
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-lg flex items-center justify-center">
                <button
                  onClick={() => onCopy(imagePreview.url)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white text-purple-600 p-2 rounded-full shadow-lg hover:bg-purple-50"
                  title="Copiar URL da imagem"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : imagePreview.url && !imagePreview.isValid ? (
            <div className="flex items-center justify-center h-48 bg-red-50 rounded-lg border-2 border-dashed border-red-300">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-red-600">Erro ao carregar imagem</p>
                <p className="text-xs text-red-500 mt-1">Verifique se a URL está correta</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <div className="text-center">
                <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Nenhuma imagem</p>
                <p className="text-xs text-gray-500 mt-1">Adicione uma URL para ver o preview</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resumo dos Dados */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="bg-gradient-to-r from-purple-500 to-indigo-500 h-1 -mx-6 -mt-6 mb-6 rounded-t-lg"></div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-purple-600" />
          Resumo do Especial
        </h3>

        <div className="space-y-4">
          {/* Nome */}
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <FileText className="h-4 w-4 text-gray-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nome</p>
              <p className="text-sm text-gray-900 font-medium">
                {specialData.name || 'Sem nome'}
              </p>
            </div>
            {specialData.name && (
              <button
                onClick={() => onCopy(specialData.name)}
                className="text-gray-400 hover:text-purple-600 transition-colors p-1"
                title="Copiar nome"
              >
                <Copy className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Slug */}
          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
            <Hash className="h-4 w-4 text-gray-600 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Slug</p>
              <p className="text-sm text-gray-900 font-mono">
                {specialData.slug || 'sem-slug'}
              </p>
            </div>
            {specialData.slug && (
              <button
                onClick={() => onCopy(specialData.slug)}
                className="text-gray-400 hover:text-purple-600 transition-colors p-1"
                title="Copiar slug"
              >
                <Copy className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Status e Peso */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <Star className={`h-4 w-4 ${specialData.is_popular ? 'text-yellow-500 fill-current' : 'text-gray-400'}`} />
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Popular</p>
                <p className="text-sm text-gray-900">
                  {specialData.is_popular ? 'Sim' : 'Não'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <Hash className="h-4 w-4 text-gray-600" />
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Peso</p>
                <p className="text-sm text-gray-900">
                  {specialData.weight || '0'}
                </p>
              </div>
            </div>
          </div>

          {/* Descrição */}
          {specialData.description && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Descrição</p>
              <p className="text-sm text-gray-700 leading-relaxed">
                {specialData.description}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewPanel;